import React, { useState, useEffect } from 'react';
import { apiFetch } from '../services/api_service';
import { API } from '../App';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  TrendingUp,
  TrendingDown,
  Target,
  Zap,
  DollarSign,
  Clock,
  Check,
  Plus,
  Minus,
  RefreshCw,
  Sparkles,
  Leaf,
  Users,
  Shield,
  BarChart3
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar
} from 'recharts';

/**
 * ESG Score Simulator
 * What-if scenario modeling for ESG improvements
 */
const ESGScoreSimulator = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [currentScore, setCurrentScore] = useState(null);
  const [scenarios, setScenarios] = useState([]);
  const [selectedScenarios, setSelectedScenarios] = useState([]);
  const [simulationResult, setSimulationResult] = useState(null);
  const [simulating, setSimulating] = useState(false);

  const fetchInitialData = async () => {
    setLoading(true);

    const [scoreData, scenariosData] = await Promise.all([
      apiFetch(`${API}/simulator/current-score`),
      apiFetch(`${API}/simulator/scenarios`)
    ]);

    if (!scoreData?._error && !scenariosData?._error) {
      setCurrentScore(scoreData);
      setScenarios(scenariosData.scenarios || []);
    } else {
      console.error('Failed to fetch simulator data:', scoreData?.message || scenariosData?.message);
      // Use mock data
      setCurrentScore({
        overall_score: 68.5,
        rating: "C",
        pillar_scores: {
          environmental: { score: 62, rating: "C" },
          social: { score: 72, rating: "B" },
          governance: { score: 75, rating: "B" }
        }
      });
      setScenarios([
        { id: "renewable_energy_50", name: "Increase Renewable Energy to 50%", category: "energy", cost: "$150K-$300K/year", time: "6-12 months" },
        { id: "renewable_energy_80", name: "Increase Renewable Energy to 80%", category: "energy", cost: "$400K-$800K/year", time: "12-24 months" },
        { id: "supplier_engagement", name: "Implement Supplier ESG Program", category: "emissions", cost: "$200K-$500K setup", time: "12-18 months" },
        { id: "diversity_initiative", name: "Diversity & Inclusion Program", category: "social", cost: "$100K-$250K/year", time: "24-36 months" },
        { id: "training_expansion", name: "Expand Employee Training", category: "social", cost: "$50K-$150K/year", time: "3-6 months" },
        { id: "board_refresh", name: "Increase Board Independence", category: "governance", cost: "$50K-$100K/year", time: "6-12 months" },
        { id: "energy_efficiency", name: "Energy Efficiency Program", category: "energy", cost: "$250K-$600K", time: "12-24 months" }
      ]);
    }
    setLoading(false);
  };

   
  useEffect(() => {
    fetchInitialData();
  }, []);

  const toggleScenario = (scenarioId) => {
    setSelectedScenarios(prev => 
      prev.includes(scenarioId)
        ? prev.filter(id => id !== scenarioId)
        : [...prev, scenarioId]
    );
    setSimulationResult(null);
  };

  const runSimulation = async () => {
    if (selectedScenarios.length === 0) return;
    
    setSimulating(true);
    const response = await apiFetch(`${API}/simulator/multi-scenario`, {
      method: 'POST',
      body: { scenario_ids: selectedScenarios }
    });
    if (response?._error) {
      // Generate mock simulation result
      const improvementPerScenario = 3.5;
      const newScore = Math.min(100, currentScore.overall_score + (selectedScenarios.length * improvementPerScenario));
      setSimulationResult({
        scenarios_applied: selectedScenarios.map(id => scenarios.find(s => s.id === id)?.name || id),
        current_score: currentScore.overall_score,
        projected_score: newScore,
        total_improvement: newScore - currentScore.overall_score,
        current_rating: currentScore.rating,
        projected_rating: newScore >= 85 ? "A" : newScore >= 70 ? "B" : newScore >= 55 ? "C" : "D",
        pillar_scores: {
          current: currentScore.pillar_scores,
          projected: {
            environmental: { score: Math.min(100, currentScore.pillar_scores.environmental.score + 8) },
            social: { score: Math.min(100, currentScore.pillar_scores.social.score + 5) },
            governance: { score: Math.min(100, currentScore.pillar_scores.governance.score + 3) }
          }
        },
        estimated_total_cost: `$${(selectedScenarios.length * 250000).toLocaleString()} - $${(selectedScenarios.length * 500000).toLocaleString()}`,
        roi_analysis: {
          estimated_annual_benefit: "$2,500,000",
          payback_period: "2.1 years",
          notes: "Based on industry average cost of capital improvement"
        }
      });
    } else {
      setSimulationResult(response);
    }
    setSimulating(false);
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'energy':
      case 'emissions':
        return <Leaf size={16} className="text-emerald-600" />;
      case 'social':
        return <Users size={16} className="text-blue-600" />;
      case 'governance':
        return <Shield size={16} className="text-purple-600" />;
      default:
        return <Target size={16} className="text-slate-600" />;
    }
  };

  const getRatingColor = (rating) => {
    switch (rating) {
      case 'A': return 'text-emerald-600 bg-emerald-100';
      case 'B': return 'text-blue-600 bg-blue-100';
      case 'C': return 'text-amber-600 bg-amber-100';
      case 'D': return 'text-orange-600 bg-orange-100';
      default: return 'text-red-600 bg-red-100';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <RefreshCw size={32} className="animate-spin text-emerald-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="esg-score-simulator">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button onClick={() => navigate(-1)} className="p-2 hover:bg-white rounded-lg">
          <ArrowLeft size={20} className="text-slate-600" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
            ESG Score Simulator
          </h1>
          <p className="text-sm text-slate-500">Model the impact of ESG improvements before implementing</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Current Score Card */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <h3 className="text-sm font-semibold text-slate-900 mb-4">Current ESG Score</h3>
            
            <div className="text-center mb-6">
              <div className={`inline-flex items-center justify-center w-24 h-24 rounded-full ${getRatingColor(currentScore?.rating)}`}>
                <span className="text-4xl font-bold">{currentScore?.rating}</span>
              </div>
              <p className="text-3xl font-bold text-slate-900 mt-2">
                {currentScore?.overall_score?.toFixed(1)}
              </p>
              <p className="text-sm text-slate-500">out of 100</p>
            </div>

            <div className="space-y-3">
              {Object.entries(currentScore?.pillar_scores || {}).map(([pillar, data]) => (
                <div key={pillar} className="flex items-center justify-between">
                  <span className="text-sm text-slate-600 capitalize">{pillar}</span>
                  <div className="flex items-center gap-2">
                    <div className="w-24 h-2 bg-slate-200 rounded-full overflow-hidden">
                      <div 
                        className={`h-full ${
                          data.score >= 70 ? 'bg-emerald-500' : 
                          data.score >= 50 ? 'bg-amber-500' : 'bg-red-500'
                        }`}
                        style={{ width: `${data.score}%` }}
                      />
                    </div>
                    <span className="text-sm font-medium text-slate-700 w-8">{data.score}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Simulation Result */}
          {simulationResult && (
            <div className="mt-4 bg-gradient-to-br from-emerald-600 to-emerald-700 rounded-xl p-6 text-white">
              <div className="flex items-center gap-2 mb-4">
                <Sparkles size={20} />
                <h3 className="font-semibold">Projected Impact</h3>
              </div>
              
              <div className="text-center mb-4">
                <div className="flex items-center justify-center gap-4">
                  <div>
                    <p className="text-emerald-200 text-xs">Current</p>
                    <p className="text-2xl font-bold">{simulationResult.current_score?.toFixed(1)}</p>
                  </div>
                  <TrendingUp size={24} className="text-emerald-300" />
                  <div>
                    <p className="text-emerald-200 text-xs">Projected</p>
                    <p className="text-2xl font-bold">{simulationResult.projected_score?.toFixed(1)}</p>
                  </div>
                </div>
                <p className="text-emerald-100 text-sm mt-2">
                  +{simulationResult.total_improvement?.toFixed(1)} points improvement
                </p>
              </div>

              <div className="bg-white/10 rounded-lg p-3 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-emerald-200">Rating Change</span>
                  <span>{simulationResult.current_rating} → {simulationResult.projected_rating}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-emerald-200">Est. Cost</span>
                  <span>{simulationResult.estimated_total_cost}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-emerald-200">Payback</span>
                  <span>{simulationResult.roi_analysis?.payback_period}</span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Scenario Selection */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-slate-900">Select Improvement Scenarios</h3>
              <span className="text-xs text-slate-500">{selectedScenarios.length} selected</span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
              {scenarios.map((scenario) => (
                <button
                  key={scenario.id}
                  onClick={() => toggleScenario(scenario.id)}
                  className={`p-4 rounded-xl border-2 text-left transition-all ${
                    selectedScenarios.includes(scenario.id)
                      ? 'border-emerald-500 bg-emerald-50'
                      : 'border-slate-200 hover:border-slate-300 bg-white'
                  }`}
                  data-testid={`scenario-${scenario.id}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2">
                      {getCategoryIcon(scenario.category)}
                      <span className="text-sm font-medium text-slate-900">{scenario.name}</span>
                    </div>
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                      selectedScenarios.includes(scenario.id)
                        ? 'border-emerald-500 bg-emerald-500'
                        : 'border-slate-300'
                    }`}>
                      {selectedScenarios.includes(scenario.id) && (
                        <Check size={12} className="text-white" />
                      )}
                    </div>
                  </div>
                  <div className="mt-2 flex items-center gap-4 text-xs text-slate-500">
                    <span className="flex items-center gap-1">
                      <DollarSign size={12} />
                      {scenario.cost}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock size={12} />
                      {scenario.time}
                    </span>
                  </div>
                </button>
              ))}
            </div>

            <button
              onClick={runSimulation}
              disabled={selectedScenarios.length === 0 || simulating}
              className="w-full py-3 bg-emerald-600 text-white rounded-xl font-medium hover:bg-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              data-testid="run-simulation"
            >
              {simulating ? (
                <>
                  <RefreshCw size={18} className="animate-spin" />
                  Simulating...
                </>
              ) : (
                <>
                  <Zap size={18} />
                  Run Simulation
                </>
              )}
            </button>
          </div>

          {/* Impact Visualization */}
          {simulationResult && (
            <div className="mt-4 bg-white rounded-xl border border-slate-200 p-6">
              <h3 className="text-sm font-semibold text-slate-900 mb-4">Pillar Score Impact</h3>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={[
                      {
                        name: 'Environmental',
                        current: currentScore?.pillar_scores?.environmental?.score || 0,
                        projected: simulationResult?.pillar_scores?.projected?.environmental?.score || 0
                      },
                      {
                        name: 'Social',
                        current: currentScore?.pillar_scores?.social?.score || 0,
                        projected: simulationResult?.pillar_scores?.projected?.social?.score || 0
                      },
                      {
                        name: 'Governance',
                        current: currentScore?.pillar_scores?.governance?.score || 0,
                        projected: simulationResult?.pillar_scores?.projected?.governance?.score || 0
                      }
                    ]}
                    layout="vertical"
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis type="number" domain={[0, 100]} />
                    <YAxis type="category" dataKey="name" width={100} />
                    <Tooltip />
                    <Bar dataKey="current" fill="#94A3B8" name="Current" radius={[0, 4, 4, 0]} />
                    <Bar dataKey="projected" fill="#10B981" name="Projected" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ESGScoreSimulator;
